import { type ElementType, type ReactElement, useRef, useState } from "react";
import {
  Box,
  ClickAwayListener,
  Grid,
  Grow,
  IconButton,
  Paper,
  Popper,
  type PopperProps,
  Tooltip,
  Typography,
} from "@mui/material";

import { MaterialSymbol } from "@/ui/components/MaterialSymbol/MaterialSymbol";

const sideSize = 112;

export type QuickAccessOption = {
  title: string;
  icon: ElementType;
  // No onClick: shown, but disabled
  onClick?: () => void;
  // Not read: only a missing onClick disables an option (P7.md "Found while typing")
  disabled?: boolean;
};

export const QuickAccessMenu = ({
  options,
}: {
  options: readonly QuickAccessOption[];
}) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);
  return (
    <>
      <Tooltip title="Quick Access" ref={anchorRef}>
        <IconButton
          onClick={() => setOpen(true)}
          data-testid="quickAccessButton"
        >
          <MaterialSymbol symbol="dashboard_customize" />
        </IconButton>
      </Tooltip>
      <PopperMenu
        anchorEl={anchorRef.current}
        id="quickaccess-menu"
        disablePortal
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        placement="bottom-end"
        sx={{
          width: `${sideSize * 3 + 16}px`,
        }}
      >
        <Grid container sx={{ p: 1 }}>
          {options.map((option) => (
            <Grid
              data-testid="quickAccessMenuItem"
              item
              xs={4}
              key={option.title}
              sx={{
                p: 1,
                textAlign: "center",
                height: `${sideSize}px`,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Box>
                <IconButton
                  disabled={!option.onClick}
                  color="primary"
                  onClick={() => {
                    option.onClick?.();
                    handleClose();
                  }}
                >
                  <option.icon sx={{ width: "35px", height: "35px" }} />
                </IconButton>
              </Box>
              <Typography>{option.title}</Typography>
            </Grid>
          ))}
        </Grid>
      </PopperMenu>
    </>
  );
};

export default QuickAccessMenu;

type PopperMenuProps = Omit<PopperProps, "children"> & {
  open: boolean;
  onClose: () => void;
  children: ReactElement;
};

const PopperMenu = ({ open, onClose, children, ...props }: PopperMenuProps) => {
  return (
    <Popper open={open} transition {...props}>
      {({ TransitionProps }) => (
        <Grow {...TransitionProps} style={{ transformOrigin: "right top" }}>
          <Paper elevation={8}>
            <ClickAwayListener onClickAway={onClose}>
              {children}
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
};
